// src/features/media/hooks/usePlayerTime.ts

import { useState, useEffect } from 'react';
import type { RefObject } from 'react';
import { PostMessageUtil, PlayerMessageType } from '@lib2/model/util/browser/PostMessageUtil';
import { PlayerMessageUtil } from '@lib2/model/player/postmessage/PlayerMessageUtil';

/**
 * Hook for listening to player time updates via PostMessage.
 * The embedded player sends time_change messages as the user scrubs or plays.
 * `currentTime` is null until the first message arrives — there is no
 * meaningful "current player time" before the player has reported one, and
 * consumers (Set in/out buttons) must not treat the pre-report state as 0:00.
 *
 * The player posts on every `timeupdate` (~4/s) with a fractional second, so
 * storing it raw re-rendered every consumer four times a second for a value
 * only ever read at whole-second precision. Quantizing to whole seconds and
 * bailing out on an unchanged value cuts that to one render per second.
 *
 * `sourceRef` MUST be passed whenever more than one player can be on the page:
 * time_change arrives on the shared window, so an unscoped listener reports
 * whichever player moved last. The video detail page mounts its own iframe of
 * the same media behind the clip modal — unscoped, Set captured 05:02 from that
 * background player while the modal's own preview had never played (verified
 * 2026-08-30). Omit it only for a genuinely single-player page.
 */
export function usePlayerTime(sourceRef?: RefObject<HTMLIFrameElement | null>) {
  const [currentTime, setCurrentTime] = useState<number | null>(null);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (sourceRef && event.source !== sourceRef.current?.contentWindow) return;
      const msg = PlayerMessageUtil.handleMessageEvent(event, PlayerMessageType.TimeChange);
      if (msg) {
        const second = Math.max(0, Math.floor(msg.msg.second));
        setCurrentTime((prev) => (prev === second ? prev : second));
      }
    };

    PostMessageUtil.registerListener(handler);
    return () => PostMessageUtil.removeListener(handler);
  }, [sourceRef]);

  return { currentTime };
}
